import { useState, useCallback, useMemo } from 'react';
import { useSupabaseQuery } from './useSupabaseQuery';
import type { Database } from '../lib/database.types';

export interface Property {
  id: number;
  address: string;
  city: string;
  state: string;
  price: string;
  brand: string;
  location: string;
  size: number;
  fuelVolume: number;
  cstoreSales: number;
  customerCount: number;
  profits: number;
}

export interface FilterState {
  brand: string;
  city: string;
  minSize: number;
  maxSize: number;
  minFuelVolume: number;
  maxFuelVolume: number;
  minCstoreSales: number;
  maxCstoreSales: number;
  similarityRange: number;
}

export interface ComparisonMetrics {
  sizeDiff: number;
  fuelVolumeDiff: number;
  cstoreSalesDiff: number;
  overallSimilarity: number;
}

type AnalyticsRow = Database['public']['Tables']['AnalyticsTable']['Row'];

export function useProperties() {
  const [referenceProperty, setReferenceProperty] = useState<Property | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    brand: '',
    city: '',
    minSize: 0,
    maxSize: 0,
    minFuelVolume: 0,
    maxFuelVolume: 0,
    minCstoreSales: 0,
    maxCstoreSales: 0,
    similarityRange: 0.2,
  });

  const { data: rawProperties, error, loading } = useSupabaseQuery<'AnalyticsTable'>('AnalyticsTable', {
    select: `
      ID,
      PropertyAddress,
      PropertyCity,
      PropertyState,
      SalePrice,
      Size,
      location,
      "2023FuelVolume",
      "2023Cstoresales",
      "2023grossprofit"
    `,
  });

  const properties = useMemo(() => {
    if (!rawProperties) return [];
    
    return rawProperties.map((analytics: AnalyticsRow) => ({
      id: analytics.ID,
      address: analytics.PropertyAddress || '',
      city: analytics.PropertyCity || '',
      state: analytics.PropertyState || '',
      price: analytics.SalePrice || '',
      brand: '', // Not available in current data
      location: analytics.PropertyAddress || '',
      size: analytics.Size ? parseFloat(analytics.Size.replace(/,/g, '')) || 0 : 0,
      fuelVolume: analytics["2023FuelVolume"] ? parseFloat(analytics["2023FuelVolume"].replace(/,/g, '')) || 0 : 0,
      cstoreSales: analytics["2023Cstoresales"] || 0,
      customerCount: 0, // Not available in current data
      profits: analytics["2023grossprofit"] ? parseFloat(analytics["2023grossprofit"].replace(/,/g, '')) || 0 : 0
    }));
  }, [rawProperties]);

  const calculateSimilarity = useCallback((property: Property): ComparisonMetrics => {
    if (!referenceProperty) return {
      sizeDiff: 0,
      fuelVolumeDiff: 0,
      cstoreSalesDiff: 0,
      overallSimilarity: 0
    };

    const sizeDiff = Math.abs(property.size - referenceProperty.size) / (referenceProperty.size || 1);
    const fuelVolumeDiff = Math.abs(property.fuelVolume - referenceProperty.fuelVolume) / (referenceProperty.fuelVolume || 1);
    const cstoreSalesDiff = Math.abs(property.cstoreSales - referenceProperty.cstoreSales) / (referenceProperty.cstoreSales || 1);
    
    const overallSimilarity = 1 - ((sizeDiff + fuelVolumeDiff + cstoreSalesDiff) / 3);

    return {
      sizeDiff,
      fuelVolumeDiff,
      cstoreSalesDiff,
      overallSimilarity
    };
  }, [referenceProperty]);

  const filteredProperties = useMemo(() => {
    return properties.filter(property => {
      if (property.id === referenceProperty?.id) return false;
      
      const similarity = calculateSimilarity(property);
      if (similarity.overallSimilarity < (1 - filters.similarityRange)) return false;
      
      if (filters.brand && property.brand !== filters.brand) return false;
      if (filters.city && property.city !== filters.city) return false;
      if (filters.minSize && property.size < filters.minSize) return false;
      if (filters.maxSize && property.size > filters.maxSize) return false;
      if (filters.minFuelVolume && property.fuelVolume < filters.minFuelVolume) return false;
      if (filters.maxFuelVolume && property.fuelVolume > filters.maxFuelVolume) return false;
      if (filters.minCstoreSales && property.cstoreSales < filters.minCstoreSales) return false;
      if (filters.maxCstoreSales && property.cstoreSales > filters.maxCstoreSales) return false;
      
      return true;
    });
  }, [properties, referenceProperty, filters, calculateSimilarity]);

  return {
    properties,
    filteredProperties,
    referenceProperty,
    setReferenceProperty,
    filters,
    setFilters,
    calculateSimilarity,
    error,
    loading
  };
}
